import { getAuth } from 'firebase/auth';

const API_BASE_URL = 'https://phantom-clash-api-27406592615.europe-west1.run.app';

// Simple tracking of API call count to avoid rate limiting
const apiCallTracker = {
  lastReset: Date.now(),
  callCount: 0,
  resetPeriod: 10000, // 10 seconds
  maxCallsPerPeriod: 5
};

/**
 * Helper function to check and reset API call count
 */
const checkRateLimit = () => {
  const now = Date.now();
  
  // Reset counter if period has passed
  if (now - apiCallTracker.lastReset > apiCallTracker.resetPeriod) {
    apiCallTracker.callCount = 0;
    apiCallTracker.lastReset = now;
  }
  
  // Check if we've hit the limit
  if (apiCallTracker.callCount >= apiCallTracker.maxCallsPerPeriod) {
    throw new Error('Client-side rate limit reached. Please wait before making more requests.');
  }
  
  // Increment counter
  apiCallTracker.callCount++;
  console.log(`API call ${apiCallTracker.callCount}/${apiCallTracker.maxCallsPerPeriod} in current period`);
};

/**
 * Helper function to fetch data with authentication
 */
const fetchWithAuth = async (url: string, options: RequestInit = {}) => {
  try {
    // Check client-side rate limiting
    checkRateLimit();
    
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      throw new Error('User not authenticated');
    }
    
    const token = await user.getIdToken();
    
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers
    };
    
    console.log(`Fetching: ${url}`);
    const response = await fetch(`${API_BASE_URL}${url}`, {
      ...options,
      headers
    });
    
    if (!response.ok) {
      // Handle specific HTTP status codes
      if (response.status === 429) {
        console.warn('Rate limit exceeded on server. Backing off.');
        throw new Error('Too many requests, please try again later.');
      }
      
      const errorData = await response.json().catch(() => null);
      throw new Error(
        errorData?.message || 
        `API error: ${response.status} - ${response.statusText}`
      );
    }
    
    const data = await response.json();
    console.log(`Response from ${url}:`, data);
    return data;
  } catch (error) {
    console.error(`API error for ${url}:`, error);
    throw error;
  }
};

/**
 * Get the current user's profile
 */
export const getUserProfile = async () => {
  try {
    return await fetchWithAuth('/api/users/profile');
  } catch (error) {
    // If we get a 404 or "User not found", try using POST to create the profile
    console.log('Error fetching profile:', error);
    if (error instanceof Error && 
        (error.message.includes('User not found') || 
         error.message.includes('404'))) {
      console.log('Creating user profile with POST request');
      return fetchWithAuth('/api/users/profile', {
        method: 'POST',
        body: JSON.stringify({ username: crypto.randomUUID() })
      });
    }
    throw error;
  }
};

/**
 * Get all characters for the current user
 */
export const getUserCharacters = async () => {
  try {
    const response = await fetchWithAuth('/api/characters/');
    console.log('Characters API response:', response);
    return response;
  } catch (error) {
    console.error('Error fetching characters:', error);
    // Return empty array on error to trigger character creation
    return { status: "success", data: [] };
  }
};

/**
 * Create a new character
 */
export const createCharacter = async (characterData: {
  name: string;
  type: 'Phantom' | 'Dark Cleric';
}) => {
  try {
    console.log('Creating character with data:', characterData);
    const response = await fetchWithAuth('/api/characters/', {
      method: 'POST',
      body: JSON.stringify(characterData)
    });
    console.log('Character creation response:', response);
    return response;
  } catch (error) {
    console.error('Error creating character:', error);
    throw error;
  }
};

/**
 * Get a character by ID
 */
export const getCharacter = async (characterId: string) => {
  return fetchWithAuth(`/api/characters/${characterId}`);
};

export default {
  getUserProfile,
  getUserCharacters,
  createCharacter,
  getCharacter
}; 